import * as React from "react";
import {
  getInvoice,
  toggleReoccurringInvoice,
  cancelInvoice,
  refundInvoice,
} from "../../../backend-services";
import { IInvoice } from "../../../@types";
import PageLoader from "../../../components/common/PageLoader";
import { useNavigate, useParams } from "react-router-dom";
import { Annotation } from "../../../@types/annotation";
import { ViewInvoice } from "../../../components/Sales/invoice/view/ViewInvoice";
import { showToast } from "../../../utils/toastUtils";
import { formatMoney } from "../../../utils";
import { useCompanyStore } from "../../../state/useCompanyStore";

export type MobileDataItem =
  | {
      name: any;
      quantity?: any;
      amount?: any;
    }
  | {
      name: any;
      duration?: any;
      price?: any;
    };

const ViewInvoicePage: React.FC = () => {
  const { invoiceNo } = useParams();
  if (!invoiceNo) {
    throw new Error("Invoice Id is missing");
  }
  const userStore = useCompanyStore();
  const isRetail = userStore.isRetailBusiness();
  const [invoice, setInvoice] = React.useState<IInvoice>();
  const [isLoading, setIsLoading] = React.useState(true);
  const [isRefunding, setIsRefunding] = React.useState(false);
  const [areDatesValid, setAreDatesValid] = React.useState<boolean>(true);
  const [isChecked, setIsChecked] = React.useState(false);
  const [previousChecked, setPreviousChecked] = React.useState(false);
  const [annotations, setAnnotations] = React.useState<Annotation[]>([]);
  const navigate = useNavigate();

  const handleToggleReoccurringInvoice = React.useCallback(
    async ({
      isReoccurringEndDate,
      reoccurringGroupName,
    }: {
      isReoccurringEndDate: string;
      reoccurringGroupName: string;
    }) => {
      if (!areDatesValid) {
        setIsLoading(false);
        setIsChecked(false);
        setPreviousChecked(false);
        return;
      }
      try {
        const invoice = await toggleReoccurringInvoice({
          id: invoiceNo,
          isReoccurringEndDate: isReoccurringEndDate
            ? new Date(isReoccurringEndDate).toISOString()
            : undefined,
          reoccurringGroupName,
        });
        setInvoice(invoice.invoice);
        showToast("Success! Your invoice is now set to reoccur.", "success");
      } catch (e) {
        setIsLoading(false);
        setIsChecked(false);
        setPreviousChecked(false);
      }
    },
    [invoiceNo, areDatesValid],
  );

  const handleCancelInvoice = React.useCallback(async () => {
    setIsLoading(true);
    try {
      const invoice = await cancelInvoice({ id: invoiceNo });
      setIsLoading(false);
      setInvoice(invoice);
    } catch (e) {
      setIsLoading(false);
      showToast("Failed to cancel invoice", "error");
    }
  }, [invoiceNo]);

  const handleRefundInvoice = React.useCallback(async () => {
    setIsRefunding(true);
    try {
      await refundInvoice({ id: invoiceNo });
      setIsRefunding(false);
      fetchInvoice();
      showToast("Invoice refunded successfully!", "success");
    } catch (e) {
      showToast("Failed to refund invoice.", "error");
    } finally {
      setIsRefunding(false);
    }
  }, [invoiceNo]);

  const fetchInvoice = React.useCallback(async () => {
    try {
      const data = await getInvoice(invoiceNo);
      setInvoice(data);
      const annotationsData =
        data?.metadata?.annotations || data?.annotations || [];
      setAnnotations(Array.isArray(annotationsData) ? annotationsData : []);
      setIsLoading(false);
    } catch (error) {
      showToast("Failed to load invoice", "error");
      setIsLoading(false);
    }
  }, [invoiceNo]);

  React.useEffect(() => {
    fetchInvoice();
    const intervalId = setInterval(() => {
      fetchInvoice();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [fetchInvoice]);

  let mobileData: MobileDataItem[] = [];
  let data: any[] = [];
  if (invoice && invoice.inventories && isRetail) {
    // Retail business, show inventories data
    data = invoice.inventories.flatMap((inv: any) => {
      if (Array.isArray(inv.variations) && inv.variations.length > 0) {
        return inv.variations.map((variation: any) => ({
          items: `${inv.name} - ${variation.type}`,
          quantity: variation?.quantity || 0,
          rate: formatMoney(variation?.price || "0"),
          amount: formatMoney(
            `${
              parseInt(variation.price || "0") *
              parseInt(variation.quantity || "0")
            }`,
            invoice.currency,
          ),
        }));
      }
      return {
        items: inv.name,
        quantity: inv?.quantity || 0,
        rate: formatMoney(`${parseInt(inv.rate || "0")}`, invoice.currency),
        amount: formatMoney(
          `${parseInt(inv.rate || "0") * parseInt(inv.quantity || "0")}`,
          invoice.currency,
        ),
      };
    });
  } else if (invoice && invoice.services && !isRetail) {
    // Not a retail business, show services data
    data = invoice.services.map((ser: any) => ({
      services: ser.name,
      duration: ser?.duration || "N/A",
      serviceCharge: formatMoney(
        `${parseInt(ser.price || "0")}`,
        invoice.currency,
      ),
    }));

    // Ensure inventories exist before calling flatMap
    if (invoice.inventories && Array.isArray(invoice.inventories)) {
      mobileData = invoice.inventories.flatMap((inv: any) => {
        if (Array.isArray(inv.variations) && inv.variations.length > 0) {
          return inv.variations.map((variation: any) => ({
            name: inv.name + " - " + variation.type,
            quantity: variation.quantity,
            amount: formatMoney(
              `${parseInt(variation.rate) * parseInt(variation.quantity)}`,
              invoice.currency,
            ),
          }));
        } else {
          return [
            {
              name: inv.name,
              quantity: inv.quantity,
              amount: formatMoney(
                `${parseInt(inv.rate) * parseInt(inv.quantity)}`,
                invoice.currency,
              ),
            },
          ];
        }
      });
    } else if (invoice && invoice.services && !isRetail) {
      data = invoice.services.map((ser: any) => ({
        services: ser.name,
        duration: ser?.duration == "0 hours" ? "Flat Rate" : ser.duration,
        serviceCharge: formatMoney(
          `${parseInt(ser.price || "0")}`,
          invoice.currency,
        ),
      }));

      mobileData = invoice.services.map((ser: any, i: any) => ({
        name: ser.name,
        duration: ser?.duration,
        serviceCharge: formatMoney(`${parseInt(ser.price)}`, invoice.currency),
      }));
    }
  }

  return (
    <div className="">
      {isLoading || data.length === 0 ? (
        <PageLoader></PageLoader>
      ) : (
        <ViewInvoice
          invoice={invoice}
          annotations={annotations}
          isRefunding={isRefunding}
          handleReOccurringInvoice={handleToggleReoccurringInvoice}
          handleCancelInvoice={handleCancelInvoice}
          handleRefundInvoice={handleRefundInvoice}
          fetchInvoice={fetchInvoice}
          customerId={""}
          data={data}
        />
      )}
    </div>
  );
};
export { ViewInvoicePage };
